'use client'

import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

interface SearchModeToggleProps {
  searchMode: 'vendor' | 'item'
  onModeChange: (mode: 'vendor' | 'item') => void
}

const segmentButtonSx = {
  flex: 1,
  textTransform: 'none',
  px: 0.75,
  py: 0.75,
  fontSize: '0.75rem',
  lineHeight: 1.25,
  border: 'none',
  '&.Mui-selected': {
    backgroundColor: 'primary.main',
    color: 'primary.contrastText',
    '&:hover': {
      backgroundColor: 'primary.dark',
    },
  },
  '&:not(.Mui-selected)': {
    backgroundColor: 'transparent',
    color: 'text.primary',
    '&:hover': {
      backgroundColor: 'action.selected',
    },
  },
} as const

const SearchModeToggle = ({ searchMode, onModeChange }: SearchModeToggleProps) => {
  const { t } = useTranslation('marketplace')

  return (
    <ToggleButtonGroup
      exclusive
      fullWidth
      value={searchMode}
      aria-label={t('searchModeToggleAriaLabel', 'Choose vendor or item search')}
      onChange={(_event, value: 'vendor' | 'item' | null) => {
        if (value !== null) onModeChange(value)
      }}
      sx={{
        mb: 1.5,
        borderRadius: 2,
        p: 0.8,
        bgcolor: 'action.hover',
        gap: 0.25,
        '& .MuiToggleButtonGroup-grouped': {
          border: 0,
          mx: 0,
          borderRadius: 1.5,
          '&:not(:first-of-type), &:first-of-type': {
            borderRadius: 1.5,
          },
        },
      }}>
      <ToggleButton value="vendor" sx={segmentButtonSx}>
        {t('vendorSearchMode', 'Vendor Search')}
      </ToggleButton>
      <ToggleButton
        value="item"
        sx={{
          ...segmentButtonSx,
          '&.Mui-selected .search-mode-new-badge': {
            color: 'primary.contrastText',
          },
        }}>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            flexWrap: 'wrap',
          }}>
          <span>{t('itemSearchMode', 'Item Search')}</span>
          <Typography
            className="search-mode-new-badge"
            component="span"
            variant="caption"
            sx={{
              fontSize: '0.5rem',
              fontWeight: 700,
              color: 'error.main',
              lineHeight: 1,
            }}>
            {t('itemSearchNewBadge', 'BETA')} {/* TODO : transation needs to be updated here */}
          </Typography>
        </Box>
      </ToggleButton>
    </ToggleButtonGroup>
  )
}

export { SearchModeToggle }
